package link.erpango.quip.data.db.entities

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.async.coroutines.awaitAsOneOrNull
import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.async.coroutines.awaitAsOne
import kotlinx.coroutines.flow.Flow
import link.erpango.quip.db.QuipDb
import link.erpango.quip.domain.entities.DMaintenance
import linkerpangoquipdb.Maintenance
import kotlin.coroutines.CoroutineContext

class MaintenanceDAO(
    val db: QuipDb
): IMaintenanceTable {
    override suspend fun selectMaintenance(id: Long): Maintenance? {
        return db.maintenanceQueries.select_by_id(id).awaitAsOneOrNull()
    }

    override suspend fun selectByServerId(serverId: Long): Maintenance? {
        return db.maintenanceQueries.select_by_server_id(serverId).awaitAsOneOrNull()
    }

    override suspend fun selectMaintenancesByVehicle(vin: String): List<DMaintenance> {
        val sparePartDAO = SparePartDAO(db)
        return db.maintenanceQueries.select_by_vehicle_vin(vin)
            .awaitAsList()
            .map { it.toDMaintenanceWithSpareParts(sparePartDAO) }
    }

    override suspend fun selectAllMaintenances(): List<Maintenance> {
        return db.maintenanceQueries.select_all().awaitAsList()
    }

    override suspend fun lastInsertRowId(): Long {
        return db.maintenanceQueries.last_insert_rowid().awaitAsOne()
    }

    override suspend fun updateMaintenance(
        id: Long,
        serverId: Long?,
        vehicleVin: String,
        date: Long,
        description: String,
        cost: Double,
        odometer: Long,
        mechanic: String,
        workshop: String,
        signature: String?
    ) {
        db.maintenanceQueries.update(
            id = id,
            server_id = serverId,
            vehicle_vin = vehicleVin,
            date = date,
            description = description,
            cost = cost,
            kilometers = odometer,
            mechanic = mechanic,
            workshop = workshop,
            signature = signature
        )
    }

    override suspend fun insertMaintenance(
        serverId: Long?,
        vehicleVin: String,
        date: Long,
        description: String,
        cost: Double,
        odometer: Long,
        mechanic: String,
        workshop: String,
        signature: String?
    ) {
        db.maintenanceQueries.insertMaintenance(
            server_id = serverId,
            vehicle_vin = vehicleVin,
            date = date,
            description = description,
            cost = cost,
            kilometers = odometer,
            mechanic = mechanic,
            workshop = workshop,
            signature = signature
        )
    }

    override fun observeMaintenances(coroutineContext: CoroutineContext): Flow<List<Maintenance>> {
        return db.maintenanceQueries.select_all()
            .asFlow()
            .mapToList(coroutineContext)
    }
    
    override suspend fun deleteMaintenance(id: Long) {
        db.maintenanceQueries.delete_by_id(id)
    }
    
    override suspend fun selectDistinctMechanics(): List<String> {
        return db.maintenanceQueries.select_distinct_mechanics().awaitAsList()
    }
    
    override suspend fun selectDistinctWorkshops(): List<String> {
        return db.maintenanceQueries.select_distinct_workshops().awaitAsList()
    }

    override fun observeDistinctMechanics(coroutineContext: CoroutineContext): Flow<List<String>> {
        return db.maintenanceQueries.select_distinct_mechanics()
            .asFlow()
            .mapToList(coroutineContext)
    }

    override fun observeDistinctWorkshops(coroutineContext: CoroutineContext): Flow<List<String>> {
        return db.maintenanceQueries.select_distinct_workshops()
            .asFlow()
            .mapToList(coroutineContext)
    }
}


fun Maintenance.toDMaintenance(): DMaintenance {
    return DMaintenance(
        id = server_id,
        localId = id,
        vehicleVin = vehicle_vin,
        kilometers = kilometers.toInt(),
        description = description,
        spareParts = emptyList(), // Será poblado por el DAO cuando se necesite
        cost = cost,
        date = date,
        mechanic = mechanic,
        workshop = workshop,
        signature = signature
    )
}

/**
 * Convierte un Maintenance de la BD a DMaintenance, cargando los spare parts asociados
 */
suspend fun Maintenance.toDMaintenanceWithSpareParts(sparePartDAO: SparePartDAO): DMaintenance {
    val spareParts = sparePartDAO.selectSparePartsByMaintenance(id)
    return DMaintenance(
        id = server_id,
        localId = id,
        vehicleVin = vehicle_vin,
        kilometers = kilometers.toInt(),
        description = description,
        spareParts = spareParts,
        cost = cost,
        date = date,
        mechanic = mechanic,
        workshop = workshop,
        signature = signature
    )
}